import { formatAndList } from './egsUtils';

// Each one stops the write for its room rate rather than degrading it.
export type EgsApplyBlocker =
  | 'archive_disagreement'
  | 'conflicting_slots'
  | 'currency_mismatch'
  | 'missing_cost_price'
  | 'multiple_existing_records'
  | 'payable_at_property_conflict'
  | 'payable_at_property_mismatch';

/**
 * A gate explains itself in the question it asks; a refusal must say what is wrong and where to fix it. Taking
 * the guest types at fault lets a slot-level refusal name them, where two records feeding four slots otherwise
 * leave the CM reading the Source column. A record-wide cause ignores them rather than lying about scope.
 */
export type EgsBlockerDefinition =
  | { kind: 'gate' }
  | { fixOn: 'roomRate' | 'salesforce'; kind: 'refusal'; message: (guestTypes: Array<string>) => string };

/** Falls back to the anonymous wording, so a missing slot list costs a name rather than the whole sentence. */
function describeSalesforceRows(guestTypes: Array<string>) {
  if (!guestTypes.length) {
    return 'A Salesforce row has';
  }

  return guestTypes.length === 1
    ? `The ${guestTypes[0]} row from Salesforce has`
    : `The ${formatAndList(guestTypes)} rows from Salesforce have`;
}

export const EGS_BLOCKERS: Record<EgsApplyBlocker, EgsBlockerDefinition> = {
  archive_disagreement: { kind: 'gate' },
  conflicting_slots: { kind: 'gate' },
  currency_mismatch: {
    fixOn: 'roomRate',
    kind: 'refusal',
    message: () =>
      'This room rate keeps its surcharges in another currency, and we write in AUD. Mixing the two would change the guest types we are leaving alone. Note the current amounts, delete the surcharge on the room rate, then apply again.',
  },
  missing_cost_price: {
    fixOn: 'salesforce',
    kind: 'refusal',
    message: (guestTypes) =>
      `${describeSalesforceRows(guestTypes)} a price but no Converted Cost, so margin would look higher than it is. Add the cost in Salesforce, then apply again.`,
  },
  multiple_existing_records: {
    fixOn: 'roomRate',
    kind: 'refusal',
    message: () =>
      'This room rate has more than one surcharge saved and we cannot tell which one a booking uses. Delete the ones that are no longer live, leaving one, then apply again.',
  },
  payable_at_property_conflict: { kind: 'gate' },
  // Asked through the same question as the conflict above: one flag per room rate, one answer to give.
  payable_at_property_mismatch: { kind: 'gate' },
};

export function isGateBlocker(blocker: EgsApplyBlocker) {
  return EGS_BLOCKERS[blocker].kind === 'gate';
}

/** The definition only where it is a refusal, so a caller cannot render a message for an answerable gate. */
export function getEgsRefusal(blocker: EgsApplyBlocker | undefined) {
  if (!blocker) {
    return undefined;
  }

  const definition = EGS_BLOCKERS[blocker];

  return definition.kind === 'refusal' ? definition : undefined;
}
